import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useAuthorMode from '../../../lib/helpers/useAuthorMode';
import { getTabpanelId, getFormatedTabId } from '../../../lib/helpers/tabContent';
import styles from './TabContent.module.scss';
import cx from 'classnames';
import TabService from '../../../lib/services/tabService';
import { KEYS } from '../../../lib/constants';

const TabContent = (props) => {
    const { children, tabId, title, className } = props;
    const [isActive, setIsActive] = useState(false);
    const [tabPanelContainerLoaded, setTabPanelContainerLoaded] = useState(false);
    const currentAuthorMode = useAuthorMode();
    const tabpanelId = getTabpanelId(tabId, title);
    const formattedTabId = getFormatedTabId(tabId, title);
    const thisRef = useRef(null);
    const tabRef = useRef(null);
    const tabpanelRef = useRef(null);

    useEffect(() => {
        testContainerLoaded(1);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const tabContainer = tabRef.current.closest('.tab-container');
        const firstTab = tabContainer && tabContainer.querySelector('.tab-button');
        const urlHash = window.location.hash;
        const tabIdOnHash = urlHash?.substring(1, urlHash?.length);

        if (tabPanelContainerLoaded) {
            // Create the object containing the observable for this tab container
            TabService.createTabContainer(tabContainer.id);

            // If I'm first tab or the tab referenced by the url hash set me as active tab
            if (tabRef.current === firstTab || formattedTabId === tabIdOnHash) {
                TabService.getTabContainer(tabContainer.id).setActiveTab(formattedTabId);
            }
        }
    }, [formattedTabId, tabPanelContainerLoaded]);

    // This effect is used to move the tabpanel divs into their own tabpanels container, so that
    // they are positioned underneath the tab titles
    useEffect(() => {
        const tabPanelsContainer =
            tabpanelRef.current.closest('.tabs-wrapper') &&
            tabpanelRef.current.closest('.tabs-wrapper').querySelector('.tab-panels-container');

        if (tabPanelsContainer && tabPanelContainerLoaded) {
            if (currentAuthorMode !== 'edit') {
                tabPanelsContainer.appendChild(tabpanelRef.current);
            } else {
                thisRef.current.appendChild(tabpanelRef.current);
            }
        }
    }, [currentAuthorMode, tabPanelContainerLoaded]);

    // This effect is used to toggle the tab active/inactive based on the value of the subscribed
    // observable
    useEffect(() => {
        if (tabPanelContainerLoaded) {
            const tabContainer = tabRef.current.closest('.tab-container');
            const subscription = TabService.getContainerObservable(tabContainer.id).subscribe(
                (tabTitle) => {
                    tabTitle === formattedTabId ? setIsActive(true) : setIsActive(false);
                }
            );
            return () => subscription.unsubscribe();
        }
    }, [tabPanelContainerLoaded, formattedTabId]);

    const activateTab = () => {
        const tabContainer = tabRef.current.closest('.tab-container');

        if (tabContainer) {
            TabService.getTabContainer(tabContainer.id).setActiveTab(formattedTabId);
        }
    };

    const handleTabKeyDown = (event) => {
        const allTabs = tabRef.current
            .closest('.tab-container')
            .getElementsByClassName('tab-button');
        const tab = event.target;
        const tabIndex = Array.from(allTabs).indexOf(tab);
        const previousTab = allTabs[tabIndex - 1];
        const nextTab = allTabs[tabIndex + 1];

        switch (event.key) {
            case KEYS.ARROW_LEFT:
            case KEYS.LEFT:
                event.preventDefault();
                previousTab ? previousTab.focus() : allTabs[allTabs.length - 1].focus();
                break;
            case KEYS.ARROW_RIGHT:
            case KEYS.RIGHT:
                event.preventDefault();
                nextTab ? nextTab.focus() : allTabs[0].focus();
                break;
            case KEYS.HOME:
                event.preventDefault();
                allTabs[0].focus();
                break;
            case KEYS.END:
                event.preventDefault();
                allTabs[allTabs.length - 1].focus();
                break;
            default:
                break;
        }
    };

    const testContainerLoaded = (currentAttempt) => {
        let containerExists =
            tabpanelRef.current.closest('.tabs-wrapper') &&
            tabpanelRef.current.closest('.tabs-wrapper').querySelector('.tab-panels-container');
        if (containerExists) {
            setTabPanelContainerLoaded(true);
        } else if (currentAttempt < 3) {
            setTimeout(() => testContainerLoaded(currentAttempt + 1), 250 * currentAttempt);
        }
    };

    return (
        <div className={cx(styles.root, className)} ref={thisRef}>
            <button
                aria-controls={tabpanelId}
                aria-selected={isActive}
                className={cx(
                    'tab-button',
                    styles.tab,
                    `${isActive ? styles['tab-active'] : ''}`,
                    isActive ? 'active' : ''
                )}
                onClick={activateTab}
                onFocus={activateTab}
                onKeyDown={handleTabKeyDown}
                id={formattedTabId}
                ref={tabRef}
                role="tab"
                tabIndex={isActive ? null : '-1'}
            >
                <span className={cx(styles['tab-text'])}>{title}</span>
            </button>
            <div
                aria-labelledby={formattedTabId}
                className={cx(
                    'tab-panel',
                    styles.tabpanel,
                    `${isActive ? styles['tabpanel-active'] : ''}`
                )}
                id={tabpanelId}
                ref={tabpanelRef}
                role="tabpanel"
                tabIndex="0"
            >
                {children}
            </div>
        </div>
    );
};

TabContent.displayName = 'TabContent';

TabContent.propTypes = {
    tabId: PropTypes.string,
    className: PropTypes.string,
    title: PropTypes.string.isRequired,
};

export default TabContent;
